import React, { useState, useCallback, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { 
  User, Mail, Users, MapPin, MessageCircle, Send, CheckCircle, 
  AlertCircle, Briefcase, Calendar, Plane, MessageSquare 
} from 'lucide-react';

const Booking = ({ isDarkMode = false }) => {
  const reduceMotion = useReducedMotion();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    destination: '',
    service: '',
    departureDate: '',
    returnDate: '',
    departureCity: '',
    groupSize: '1',
    specialRequests: ''
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor;
    const previousColor = document.body.style.color;
    const previousColorScheme = document.body.style.colorScheme;

    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

    return () => {
      document.body.style.backgroundColor = previousBackground;
      document.body.style.color = previousColor;
      document.body.style.colorScheme = previousColorScheme;
    };
  }, [isDarkMode]);

  const validate = useCallback(() => {
    const errs = {};
    if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Valid email required';
    }
    if (!formData.phone.trim()) errs.phone = 'Phone number required';
    if (!formData.destination.trim()) errs.destination = 'Destination required';
    if (!formData.service) errs.service = 'Please select a service';
    if (!formData.departureDate) errs.departureDate = 'Departure date required';
    if (formData.departureDate && formData.returnDate && formData.returnDate < formData.departureDate) {
      errs.returnDate = 'Return date must be after departure';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const createWhatsAppURL = () => {
    const msg = `*New Booking Request*

*Name:* ${formData.fullName}
*Email:* ${formData.email || '—'}
*Phone:* ${formData.phone}
*Service:* ${formData.service}
*Destination:* ${formData.destination}
*Departure City:* ${formData.departureCity || '—'}
*Departure Date:* ${formData.departureDate}
*Return Date:* ${formData.returnDate || 'One-way / Flexible'}
*Group Size:* ${formData.groupSize} ${formData.groupSize === '1' ? 'person' : 'people'}

*Special Requests:*
${formData.specialRequests || 'None'}

_Sent via Oscar Global Web_`;

    return `https://wa.me/2348077854009?text=${encodeURIComponent(msg)}`;
  };

  const safelyOpenWhatsApp = (url) => {
    try {
      const win = window.open(url, '_blank', 'noopener,noreferrer,width=540,height=800');
      if (win) {
        win.focus();
        return true;
      }
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 50);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    const url = createWhatsAppURL();
    const success = safelyOpenWhatsApp(url);

    setStatus(success ? 'success' : 'error');
    if (success) {
      setFormData({
        fullName: '', email: '', phone: '', destination: '',
        service: '', departureDate: '', returnDate: '', departureCity: '', groupSize: '1', specialRequests: ''
      });
      setErrors({});
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const heroReveal = reduceMotion ? {} : {
    initial: { opacity: 0, y: 26 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  };

  const formReveal = reduceMotion ? {} : {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

    .booking-root {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: var(--bg);
      color: var(--text);
      min-height: 100vh;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }

    .booking-root * { box-sizing: border-box; }
    .booking-root h1, .booking-root h2 { font-family: 'Sora', sans-serif; color: var(--text); }
    .booking-root p { color: var(--text-muted); }

    .booking-root :focus-visible {
      outline: 3px solid var(--focus);
      outline-offset: 2px;
      border-radius: 8px;
    }

    .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
    .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
    .sectiona { margin-top: 4rem; }
    .hero {
      padding-top: clamp(8rem, 12vw, 10rem);
      text-align: center;
      margin-bottom: 2rem;
    }

    .eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 0.55rem;
      padding: 0.48rem 0.85rem;
      border-radius: 999px;
      border: 1px solid var(--border-strong);
      background: var(--surface);
      color: var(--primary);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    .h1 {
      font-size: clamp(2.5rem, 5vw, 3.75rem);
      line-height: 1.1;
      letter-spacing: -0.03em;
      margin: 1.25rem 0 1.5rem;
      font-weight: 700;
    }

    .lead {
      font-size: 1.125rem;
      line-height: 1.7;
      color: var(--text-muted);
      max-width: 640px;
      margin: 0 auto;
    }

    .form-card {
      max-width: 860px;
      margin: 0 auto;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 28px;
      padding: clamp(2rem, 5vw, 3.5rem);
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    @media (min-width: 640px) {
      .form-grid { grid-template-columns: 1fr 1fr; }
      .form-group.full { grid-column: 1 / -1; }
    }

    .form-group { display: flex; flex-direction: column; gap: 0.5rem; }

    .form-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--text);
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .form-label .req { color: var(--accent); }

    .form-input, .form-select, .form-textarea {
      width: 100%;
      padding: 0.875rem 1rem;
      border-radius: 14px;
      border: 1px solid var(--border-strong);
      background: var(--bg-muted);
      color: var(--text);
      font-size: 0.95rem;
      font-family: inherit;
      transition: all 0.2s ease;
      outline: none;
    }

    .form-input:focus, .form-select:focus, .form-textarea:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 4px var(--focus);
      background: var(--surface);
    }

    .form-input.error, .form-select.error, .form-textarea.error {
      border-color: #ef4444;
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
    }

    .form-select {
      appearance: none;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 1rem center;
      background-size: 16px;
      padding-right: 2.5rem;
    }

    .form-textarea { resize: vertical; min-height: 120px; }

    .form-error {
      font-size: 0.8rem;
      color: #ef4444;
      display: flex;
      align-items: center;
      gap: 0.35rem;
      margin-top: -0.25rem;
    }

    .btn-submit {
      width: 100%;
      padding: 1rem;
      border-radius: 999px;
      background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
      color: #fff;
      font-size: 1rem;
      font-weight: 700;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.6rem;
      box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
      transition: all 0.25s ease;
      margin-top: 1rem;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
    }

    .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

    .status-msg {
      padding: 1rem 1.25rem;
      border-radius: 16px;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      font-size: 0.9rem;
      font-weight: 500;
      line-height: 1.5;
    }

    .status-msg.success {
      background: rgba(16, 185, 129, 0.08);
      border: 1px solid rgba(16, 185, 129, 0.25);
      color: #10b981;
    }

    .status-msg.error {
      background: rgba(239, 68, 68, 0.08);
      border: 1px solid rgba(239, 68, 68, 0.25);
      color: #ef4444;
    }

    .status-msg a {
      color: inherit;
      text-decoration: underline;
      font-weight: 600;
    }

    .trust-footer {
      margin-top: 2.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--border);
      text-align: center;
      font-size: 0.85rem;
      color: var(--text-muted);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner {
      width: 18px; height: 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @media (max-width: 640px) {
      .container { width: calc(100% - 2rem); }
    }
  `;

  return (
    <main
      className="booking-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--accent': isDarkMode ? '#F59E0B' : '#B45309',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
      }}
    >
      <style>{globalStyles}</style>

      <section className="section sectiona hero">
        <div className="container">
          <motion.div {...heroReveal}>
            <span className="eyebrow">Start Your Journey</span>
            <h1 className="h1">Book Your Trip</h1>
            <p className="lead">
              Tell us about your travel plans. Your request is sent directly to our team 
              for immediate, personalized assistance.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <motion.div className="form-card" {...formReveal}>
            
            {status === 'success' && (
              <div className="status-msg success" role="alert">
                <CheckCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Booking request sent!</strong><br />
                  Your inquiry is open in WhatsApp. Tap <strong>Send</strong> to confirm with our team.
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="status-msg error" role="alert">
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Failed to open WhatsApp.</strong><br />
                  Ensure pop-ups are allowed, or <a href="https://wa.me/2348077854009" target="_blank" rel="noopener noreferrer">contact us manually</a>.
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-grid">
                
                <div className="form-group">
                  <label htmlFor="fullName" className="form-label">
                    <User size={16} /> Full Name <span className="req">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Adaobi Chukwu"
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                  />
                  {errors.fullName && <span className="form-error"><AlertCircle size={14} /> {errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <Mail size={16} /> Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="adaobi@example.com"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <span className="form-error"><AlertCircle size={14} /> {errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <MessageCircle size={16} /> Phone <span className="req">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+234 80X XXX XXXX"
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                  />
                  {errors.phone && <span className="form-error"><AlertCircle size={14} /> {errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="service" className="form-label">
                    <Briefcase size={16} /> Service Type <span className="req">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`form-select ${errors.service ? 'error' : ''}`}
                  >
                    <option value="">Select a service</option>
                    <option value="corporate">Corporate & Executive Travel</option>
                    <option value="cruise">Cruises & Leisure Getaways</option>
                    <option value="honeymoon">Romantic Honeymoons</option>
                    <option value="student">Student Admission Placement</option>
                    <option value="ticketing">Ticketing</option>
                    <option value="visa">Visa Application Support</option>
                    <option value="other">Other Requests</option>
                  </select>
                  {errors.service && <span className="form-error"><AlertCircle size={14} /> {errors.service}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="departureCity" className="form-label">
                    <Plane size={16} /> Departure City
                  </label>
                  <input
                    id="departureCity"
                    name="departureCity"
                    type="text"
                    value={formData.departureCity}
                    onChange={handleChange}
                    placeholder="e.g. Lagos, Abuja"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="destination" className="form-label">
                    <MapPin size={16} /> Destination <span className="req">*</span>
                  </label>
                  <input
                    id="destination"
                    name="destination"
                    type="text"
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="e.g. Dubai, Maldives"
                    className={`form-input ${errors.destination ? 'error' : ''}`}
                  />
                  {errors.destination && <span className="form-error"><AlertCircle size={14} /> {errors.destination}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="departureDate" className="form-label">
                    <Calendar size={16} /> Departure Date <span className="req">*</span>
                  </label>
                  <input
                    id="departureDate"
                    name="departureDate"
                    type="date"
                    value={formData.departureDate}
                    onChange={handleChange}
                    className={`form-input ${errors.departureDate ? 'error' : ''}`}
                  />
                  {errors.departureDate && <span className="form-error"><AlertCircle size={14} /> {errors.departureDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="returnDate" className="form-label">
                    <Calendar size={16} /> Return Date
                  </label>
                  <input
                    id="returnDate"
                    name="returnDate"
                    type="date"
                    value={formData.returnDate}
                    onChange={handleChange}
                    className={`form-input ${errors.returnDate ? 'error' : ''}`}
                  />
                  {errors.returnDate && <span className="form-error"><AlertCircle size={14} /> {errors.returnDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="groupSize" className="form-label">
                    <Users size={16} /> No of Travelers 
                  </label>
                  <select
                    id="groupSize"
                    name="groupSize"
                    value={formData.groupSize}
                    onChange={handleChange}
                    className="form-select"
                  >
                    {[1,2,3,4,5,6,7,8,9,'10'].map(n => (
                      <option key={n} value={n}>{n} {n === '10' ? 'or more' : n > 1 ? 'people' : 'person'}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full">
                  <label htmlFor="specialRequests" className="form-label">
                    <MessageSquare size={16} /> Other Requests
                  </label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    placeholder="e.g. Dietary needs, accessibility, celebration, etc."
                    className="form-textarea"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-submit"
              >
                {status === 'submitting' ? (
                  <><span className="spinner" /> Opening WhatsApp…</>
                ) : (
                  <><Send size={18} /> Send Request via WhatsApp</>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Booking;





























// import React, { useState, useCallback, useEffect } from 'react';
// import { motion, useReducedMotion } from 'framer-motion';
// import { 
//   User, Mail, Users, MapPin, MessageCircle, Send, CheckCircle, 
//   AlertCircle, Briefcase, Calendar, Plane, MessageSquare 
// } from 'lucide-react';

// const Booking = ({ isDarkMode = false }) => {
//   const reduceMotion = useReducedMotion();
//   const [formData, setFormData] = useState({
//     fullName: '',
//     email: '',
//     phone: '',
//     destination: '',
//     service: '',
//     travelDate: '',
//     departureCity: '',
//     groupSize: '1',
//     specialRequests: ''
//   });
//   const [errors, setErrors] = useState({});
//   const [status, setStatus] = useState('idle');

//   useEffect(() => {
//     const previousBackground = document.body.style.backgroundColor;
//     const previousColor = document.body.style.color;
//     const previousColorScheme = document.body.style.colorScheme;

//     document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
//     document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
//     document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

//     return () => {
//       document.body.style.backgroundColor = previousBackground;
//       document.body.style.color = previousColor;
//       document.body.style.colorScheme = previousColorScheme;
//     };
//   }, [isDarkMode]);

//   const validate = useCallback(() => {
//     const errs = {};
//     if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
//     if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
//       errs.email = 'Valid email required';
//     }
//     if (!formData.phone.trim()) errs.phone = 'Phone number required';
//     if (!formData.destination.trim()) errs.destination = 'Destination required';
//     if (!formData.service) errs.service = 'Please select a service';
//     if (!formData.travelDate) errs.travelDate = 'Travel date required';
//     setErrors(errs);
//     return Object.keys(errs).length === 0;
//   }, [formData]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//     if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
//   };

//   const createWhatsAppURL = () => {
//     const msg = `*New Booking Request*

// *Name:* ${formData.fullName}
// *Email:* ${formData.email || '—'}
// *Phone:* ${formData.phone}
// *Service:* ${formData.service}
// *Destination:* ${formData.destination}
// *Departure:* ${formData.departureCity || '—'}
// *Date:* ${formData.travelDate}
// *Group Size:* ${formData.groupSize} ${formData.groupSize === '1' ? 'person' : 'people'}

// *Special Requests:*
// ${formData.specialRequests || 'None'}

// _Sent via Oscar Global Web_`;

//     return `https://wa.me/2348077854009?text=${encodeURIComponent(msg)}`;
//   };

//   const safelyOpenWhatsApp = (url) => {
//     try {
//       const win = window.open(url, '_blank', 'noopener,noreferrer,width=540,height=800');
//       if (win) {
//         win.focus();
//         return true;
//       }
//       const a = document.createElement('a');
//       a.href = url;
//       a.target = '_blank';
//       a.rel = 'noopener noreferrer';
//       a.style.display = 'none';
//       document.body.appendChild(a);
//       a.click();
//       setTimeout(() => document.body.removeChild(a), 50);
//       return true;
//     } catch {
//       return false;
//     }
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!validate()) return;

//     setStatus('submitting');
//     const url = createWhatsAppURL();
//     const success = safelyOpenWhatsApp(url);

//     setStatus(success ? 'success' : 'error');
//     if (success) {
//       setFormData({
//         fullName: '', email: '', phone: '', destination: '',
//         service: '', travelDate: '', departureCity: '', groupSize: '1', specialRequests: ''
//       });
//       setErrors({});
//       setTimeout(() => setStatus('idle'), 4000);
//     }
//   };

//   const heroReveal = reduceMotion ? {} : {
//     initial: { opacity: 0, y: 26 },
//     animate: { opacity: 1, y: 0 },
//     transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
//   };

//   const formReveal = reduceMotion ? {} : {
//     initial: { opacity: 0, y: 30 },
//     whileInView: { opacity: 1, y: 0 },
//     viewport: { once: true },
//     transition: { duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }
//   };

//   const globalStyles = `
//     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

//     .booking-root {
//       font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
//       background: var(--bg);
//       color: var(--text);
//       min-height: 100vh;
//       overflow-x: hidden;
//       -webkit-font-smoothing: antialiased;
//       text-rendering: optimizeLegibility;
//     }

//     .booking-root * { box-sizing: border-box; }
//     .booking-root h1, .booking-root h2 { font-family: 'Sora', sans-serif; color: var(--text); }
//     .booking-root p { color: var(--text-muted); }

//     .booking-root :focus-visible {
//       outline: 3px solid var(--focus);
//       outline-offset: 2px;
//       border-radius: 8px;
//     }

//     .container { width: min(1180px, calc(100% - 2.5rem)); margin-inline: auto; }
//     .section { padding-block: clamp(4rem, 8vw, 6.75rem); }
//     .section { margin-top: 4rem}
//     .hero {
//       padding-top: clamp(8rem, 12vw, 10rem);
//       text-align: center;
//       margin-bottom: 2rem;
//     }

//     .eyebrow {
//       display: inline-flex;
//       align-items: center;
//       gap: 0.55rem;
//       padding: 0.48rem 0.85rem;
//       border-radius: 999px;
//       border: 1px solid var(--border-strong);
//       background: var(--surface);
//       color: var(--primary);
//       font-size: 0.78rem;
//       font-weight: 700;
//       letter-spacing: 0.14em;
//       text-transform: uppercase;
//     }

//     .h1 {
//       font-size: clamp(2.5rem, 5vw, 3.75rem);
//       line-height: 1.1;
//       letter-spacing: -0.03em;
//       margin: 1.25rem 0 1.5rem;
//       font-weight: 700;
//     }

//     .lead {
//       font-size: 1.125rem;
//       line-height: 1.7;
//       color: var(--text-muted);
//       max-width: 640px;
//       margin: 0 auto;
//     }

//     .form-card {
//       max-width: 860px;
//       margin: 0 auto;
//       background: var(--surface);
//       border: 1px solid var(--border);
//       border-radius: 28px;
//       padding: clamp(2rem, 5vw, 3.5rem);
//       box-shadow: var(--shadow);
//       backdrop-filter: blur(14px);
//     }

//     .form-grid {
//       display: grid;
//       grid-template-columns: 1fr;
//       gap: 1.5rem;
//     }

//     @media (min-width: 640px) {
//       .form-grid { grid-template-columns: 1fr 1fr; }
//       .form-group.full { grid-column: 1 / -1; }
//     }

//     .form-group { display: flex; flex-direction: column; gap: 0.5rem; }

//     .form-label {
//       font-size: 0.875rem;
//       font-weight: 600;
//       color: var(--text);
//       display: flex;
//       align-items: center;
//       gap: 0.5rem;
//     }

//     .form-label .req { color: var(--accent); }

//     .form-input, .form-select, .form-textarea {
//       width: 100%;
//       padding: 0.875rem 1rem;
//       border-radius: 14px;
//       border: 1px solid var(--border-strong);
//       background: var(--bg-muted);
//       color: var(--text);
//       font-size: 0.95rem;
//       font-family: inherit;
//       transition: all 0.2s ease;
//       outline: none;
//     }

//     .form-input:focus, .form-select:focus, .form-textarea:focus {
//       border-color: var(--primary);
//       box-shadow: 0 0 0 4px var(--focus);
//       background: var(--surface);
//     }

//     .form-input.error, .form-select.error, .form-textarea.error {
//       border-color: #ef4444;
//       box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
//     }

//     .form-select {
//       appearance: none;
//       background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e");
//       background-repeat: no-repeat;
//       background-position: right 1rem center;
//       background-size: 16px;
//       padding-right: 2.5rem;
//     }

//     .form-textarea { resize: vertical; min-height: 120px; }

//     .form-error {
//       font-size: 0.8rem;
//       color: #ef4444;
//       display: flex;
//       align-items: center;
//       gap: 0.35rem;
//       margin-top: -0.25rem;
//     }

//     .btn-submit {
//       width: 100%;
//       padding: 1rem;
//       border-radius: 999px;
//       background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
//       color: #fff;
//       font-size: 1rem;
//       font-weight: 700;
//       border: none;
//       cursor: pointer;
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       gap: 0.6rem;
//       box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
//       transition: all 0.25s ease;
//       margin-top: 1rem;
//     }

//     .btn-submit:hover:not(:disabled) {
//       transform: translateY(-2px);
//       box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
//     }

//     .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

//     .status-msg {
//       padding: 1rem 1.25rem;
//       border-radius: 16px;
//       display: flex;
//       align-items: flex-start;
//       gap: 0.75rem;
//       margin-bottom: 1.5rem;
//       font-size: 0.9rem;
//       font-weight: 500;
//       line-height: 1.5;
//     }

//     .status-msg.success {
//       background: rgba(16, 185, 129, 0.08);
//       border: 1px solid rgba(16, 185, 129, 0.25);
//       color: #10b981;
//     }

//     .status-msg.error {
//       background: rgba(239, 68, 68, 0.08);
//       border: 1px solid rgba(239, 68, 68, 0.25);
//       color: #ef4444;
//     }

//     .status-msg a {
//       color: inherit;
//       text-decoration: underline;
//       font-weight: 600;
//     }

//     .trust-footer {
//       margin-top: 2.5rem;
//       padding-top: 1.5rem;
//       border-top: 1px solid var(--border);
//       text-align: center;
//       font-size: 0.85rem;
//       color: var(--text-muted);
//       display: flex;
//       align-items: center;
//       justify-content: center;
//       gap: 0.5rem;
//     }

//     @keyframes spin { to { transform: rotate(360deg); } }
//     .spinner {
//       width: 18px; height: 18px;
//       border: 2px solid rgba(255,255,255,0.3);
//       border-top-color: #fff;
//       border-radius: 50%;
//       animation: spin 0.8s linear infinite;
//     }

//     @media (max-width: 640px) {
//       .container { width: calc(100% - 2rem); }
//     }
//   `;

//   return (
//     <main
//       className="booking-root"
//       style={{
//         '--bg': isDarkMode ? '#061110' : '#F7FBFA',
//         '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
//         '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
//         '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
//         '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
//         '--text': isDarkMode ? '#E9F6F3' : '#132E29',
//         '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
//         '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
//         '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
//         '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
//         '--accent': isDarkMode ? '#F59E0B' : '#B45309',
//         '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.25)' : 'rgba(15, 118, 110, 0.15)',
//         '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
//       }}
//     >
//       <style>{globalStyles}</style>

//       <section className="section sectiona hero">
//         <div className="container">
//           <motion.div {...heroReveal}>
//             <span className="eyebrow">Start Your Journey</span>
//             <h1 className="h1">Book Your Trip</h1>
//             <p className="lead">
//               Tell us about your travel plans. Your request is sent directly to our team 
//               for immediate, personalized assistance.
//             </p>
//           </motion.div>
//         </div>
//       </section>

//       <section className="section" style={{ paddingTop: 0 }}>
//         <div className="container">
//           <motion.div className="form-card" {...formReveal}>
            
//             {status === 'success' && (
//               <div className="status-msg success" role="alert">
//                 <CheckCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
//                 <div>
//                   <strong>Booking request sent!</strong><br />
//                   Your inquiry is open in WhatsApp. Tap <strong>Send</strong> to confirm with our team.
//                 </div>
//               </div>
//             )}

//             {status === 'error' && (
//               <div className="status-msg error" role="alert">
//                 <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
//                 <div>
//                   <strong>Failed to open WhatsApp.</strong><br />
//                   Ensure pop-ups are allowed, or <a href="https://wa.me/2348077854009" target="_blank" rel="noopener noreferrer">contact us manually</a>.
//                 </div>
//               </div>
//             )}

//             <form onSubmit={handleSubmit} noValidate>
//               <div className="form-grid">
                
//                 <div className="form-group">
//                   <label htmlFor="fullName" className="form-label">
//                     <User size={16} /> Full Name <span className="req">*</span>
//                   </label>
//                   <input
//                     id="fullName"
//                     name="fullName"
//                     type="text"
//                     value={formData.fullName}
//                     onChange={handleChange}
//                     placeholder="e.g. Adaobi Chukwu"
//                     className={`form-input ${errors.fullName ? 'error' : ''}`}
//                   />
//                   {errors.fullName && <span className="form-error"><AlertCircle size={14} /> {errors.fullName}</span>}
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="email" className="form-label">
//                     <Mail size={16} /> Email
//                   </label>
//                   <input
//                     id="email"
//                     name="email"
//                     type="email"
//                     value={formData.email}
//                     onChange={handleChange}
//                     placeholder="adaobi@example.com"
//                     className={`form-input ${errors.email ? 'error' : ''}`}
//                   />
//                   {errors.email && <span className="form-error"><AlertCircle size={14} /> {errors.email}</span>}
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="phone" className="form-label">
//                     <MessageCircle size={16} /> Phone <span className="req">*</span>
//                   </label>
//                   <input
//                     id="phone"
//                     name="phone"
//                     type="tel"
//                     value={formData.phone}
//                     onChange={handleChange}
//                     placeholder="+234 80X XXX XXXX"
//                     className={`form-input ${errors.phone ? 'error' : ''}`}
//                   />
//                   {errors.phone && <span className="form-error"><AlertCircle size={14} /> {errors.phone}</span>}
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="service" className="form-label">
//                     <Briefcase size={16} /> Service Type <span className="req">*</span>
//                   </label>
//                   <select
//                     id="service"
//                     name="service"
//                     value={formData.service}
//                     onChange={handleChange}
//                     className={`form-select ${errors.service ? 'error' : ''}`}
//                   >
//                     <option value="">Select a service</option>
//                     <option value="corporate">Corporate & Executive Travels</option>
//                     <option value="cruise">Cruises & Leisure Getaways</option>
//                     <option value="honeymoon">Romantic Honeymoons</option>
//                     <option value="student">Student Admission Placement</option>
//                     <option value="honeymoon">Ticketing</option>
//                     <option value="visa">Visa Application Support</option>
//                     <option value="other">Other Requests</option>
//                   </select>
//                   {errors.service && <span className="form-error"><AlertCircle size={14} /> {errors.service}</span>}
//                 </div>

                

//                 <div className="form-group">
//                   <label htmlFor="travelDate" className="form-label">
//                     <Calendar size={16} /> Travel Date <span className="req">*</span>
//                   </label>
//                   <input
//                     id="travelDate"
//                     name="travelDate"
//                     type="date"
//                     value={formData.travelDate}
//                     onChange={handleChange}
//                     className={`form-input ${errors.travelDate ? 'error' : ''}`}
//                   />
//                   {errors.travelDate && <span className="form-error"><AlertCircle size={14} /> {errors.travelDate}</span>}
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="departureCity" className="form-label">
//                     <Plane size={16} /> Departure City
//                   </label>
//                   <input
//                     id="departureCity"
//                     name="departureCity"
//                     type="text"
//                     value={formData.departureCity}
//                     onChange={handleChange}
//                     placeholder="e.g. Lagos, Abuja"
//                     className="form-input"
//                   />
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="destination" className="form-label">
//                     <MapPin size={16} /> Destination <span className="req">*</span>
//                   </label>
//                   <input
//                     id="destination"
//                     name="destination"
//                     type="text"
//                     value={formData.destination}
//                     onChange={handleChange}
//                     placeholder="e.g. Dubai, Maldives"
//                     className={`form-input ${errors.destination ? 'error' : ''}`}
//                   />
//                   {errors.destination && <span className="form-error"><AlertCircle size={14} /> {errors.destination}</span>}
//                 </div>

//                 <div className="form-group">
//                   <label htmlFor="groupSize" className="form-label">
//                     <Users size={16} /> No of Travelers 
//                   </label>
//                   <select
//                     id="groupSize"
//                     name="groupSize"
//                     value={formData.groupSize}
//                     onChange={handleChange}
//                     className="form-select"
//                   >
//                     {[1,2,3,4,5,6,7,8,9,'10'].map(n => (
//                       <option key={n} value={n}>{n} {n === '10' ? 'or more' : n > 1 ? 'people' : 'person'}</option>
//                     ))}
//                   </select>
//                 </div>

//                 <div className="form-group full">
//                   <label htmlFor="specialRequests" className="form-label">
//                     <MessageSquare size={16} /> Other Requests
//                   </label>
//                   <textarea
//                     id="specialRequests"
//                     name="specialRequests"
//                     value={formData.specialRequests}
//                     onChange={handleChange}
//                     placeholder="e.g. Dietary needs, accessibility, celebration, etc."
//                     className="form-textarea"
//                   />
//                 </div>
//               </div>

//               <button
//                 type="submit"
//                 disabled={status === 'submitting'}
//                 className="btn-submit"
//               >
//                 {status === 'submitting' ? (
//                   <><span className="spinner" /> Opening WhatsApp…</>
//                 ) : (
//                   <><Send size={18} /> Send Request via WhatsApp</>
//                 )}
//               </button>
//             </form>
// {/* 
//             <div className="trust-footer">
//               <ShieldCheck size={16} />
//               <span>Secure & Private: Your details are sent directly via encrypted WhatsApp.</span>
//             </div> */}
//           </motion.div>
//         </div>
//       </section>
//     </main>
//   );
// };

// export default Booking;